import React from 'react';
import { createSelector } from 'reselect';
import { v1 as uuidv1 } from 'uuid';

import { ProductItem } from 'containers/Col2/Products';
import { ComponentConfiguration } from 'plugins/customButtons/Configuration';
import useProducts from 'utils/hooks/useProducts';

import { PosPlugin } from '../plugin';
import { getPluginConfiguration } from '../../reducers/Plugins';
import { pluginID } from './constants';

export type Configuration = {
  buttons: {
    name: string;
    url: string;
    enabled: boolean;
  }[];
  products: {
    productID: string;
    enabled: true;
  }[];
};

const LoadingProductItem = ({ productID }) => {
  const {
    products: [product],
  } = useProducts({ productID, getPriceListPrices: 1 });

  return (
    <ProductItem
      item={product ?? { productID, name: 'Loading...', price: '0.00' }}
    />
  );
};

const getConfiguration = state =>
  getPluginConfiguration<Configuration>('customButtons')(state);
const customButtonsPlugin: PosPlugin = {
  id: pluginID,
  name: 'Custom POS buttons',
  // language=Markdown
  info: `Enables adding custom buttons: URL buttons to the saleOptions section, as well as product buttons to the functionButtons section`,
  keywords: ['common', 'UI', 'saleOptions', 'grid', 'buttons', 'add', 'product', 'url', 'link', 'web'],
  getStatus: createSelector(getConfiguration, (conf: Configuration) => {
    if (!conf) return { type: 'error', message: 'No configuration' };
    if (!conf?.buttons?.length && !conf?.products?.length)
      return { type: 'error', message: 'No buttons configured' };
    if (
      !conf?.buttons?.some(btn => btn.enabled) &&
      !conf.products?.some(btn => btn.enabled)
    )
      return { type: 'warning', message: 'No buttons enabled' };
    return { type: 'valid', message: '' };
  }),
  selectorOverrides: {
    getFunctionButtons: base =>
      createSelector(base, getConfiguration, (buttons, conf) =>
        buttons.concat(
          conf?.buttons
            .filter(b => b.enabled)
            .map(
              b =>
                ({
                  id: uuidv1(),
                  name: b.name,
                  actionType: 'action',
                  action: () => window.open(b.url),
                } ?? []),
            ),
        ),
      ),
    getSaleOptionButtons: base =>
      createSelector(base, getConfiguration, (buttons, conf) =>
        buttons.concat(
          conf?.products
            ?.filter(b => b.enabled)
            .map(p => p.productID)
            .map(id => ({
              id,
              view: <LoadingProductItem productID={id} />,
            })) ?? [],
        ),
      ),
  },
  ComponentConfiguration,
};
export default customButtonsPlugin;
